import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { CurrencyDollarIcon as CurrencyDollar, CopyIcon as Copy, ShareNetworkIcon as ShareNetwork, CheckCircleIcon as CheckCircle, UsersIcon as Users } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";

export default function ReferralProgram() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/me/referral").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const copy = async () => {
    if (!data?.share_url) return;
    await navigator.clipboard.writeText(data.share_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!data) return <AppShell><div className="text-[#6B6B6B]">Loading…</div></AppShell>;

  return (
    <AppShell>
      <div className="mb-8 max-w-3xl">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">Earn</p>
        <h1 className="font-spectral text-4xl tracking-tight">Refer & earn 1%</h1>
        <p className="text-[#6B6B6B] mt-2">
          Share your link with anyone hunting for a great breeder. When they buy a dog through the marketplace, you earn 1% — paid out via Stripe automatically.
        </p>
      </div>

      <div className="bg-[#0E0E0E] text-white rounded-xl p-6 mb-6 max-w-3xl" data-testid="referral-link-card">
        <div className="text-xs uppercase tracking-[0.2em] text-white/60 mb-2">Your share link</div>
        <div className="flex items-center gap-2 bg-white/10 rounded-lg p-3 font-mono text-sm break-all">
          {data.share_url}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={copy} className="rounded-full bg-[#C9A227] hover:bg-white hover:text-ink text-white" data-testid="referral-copy-btn">
            {copied ? <><CheckCircle size={14} className="mr-1.5" weight="fill" />Copied</> : <><Copy size={14} className="mr-1.5" />Copy link</>}
          </Button>
          {navigator.share && (
            <Button
              onClick={() => navigator.share({ url: data.share_url, title: "Find your next dog with Armstrong K9 Registry" })}
              variant="outline"
              className="rounded-full border-white/20 text-white hover:bg-white/10"
              data-testid="referral-native-share-btn"
            >
              <ShareNetwork size={14} className="mr-1.5" /> Share
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 max-w-3xl">
        <Stat label="Visits" value={data.visits} icon={Users} />
        <Stat label="Conversions" value={data.conversions} icon={CheckCircle} />
        <Stat label="Pending" value={`$${(data.pending_commission || 0).toFixed(2)}`} icon={CurrencyDollar} />
        <Stat label="Earned" value={`$${(data.earned || 0).toFixed(2)}`} icon={CurrencyDollar} />
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 max-w-3xl">
        <h2 className="font-spectral text-2xl tracking-tight mb-3">How it works</h2>
        <ol className="list-decimal pl-5 space-y-1.5 text-sm text-ink">
          <li>Share your link wherever — Instagram, breed forums, dog-show groups.</li>
          <li>When a new buyer clicks the link, we tag their browser as referred by you.</li>
          <li>If they complete a marketplace purchase, you earn <strong>1% of the gross</strong>.</li>
          <li>Commissions are paid out monthly via your <Link to="/payouts" className="font-semibold hover:text-crimson">Stripe Connect account</Link>.</li>
        </ol>
      </div>
    </AppShell>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-xl p-5">
      <Icon size={20} weight="duotone" className="text-gold mb-2" />
      <div className="text-xs uppercase tracking-wider text-[#6B6B6B]">{label}</div>
      <div className="font-spectral text-2xl tracking-tight mt-1">{value}</div>
    </div>
  );
}
